import { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, CloseButton, Dialog, Field, HStack, Input, Text, VStack,
} from '@chakra-ui/react'
import { FiDownload, FiPrinter } from 'react-icons/fi'
import { ResponsiveModal, QrCode } from '../../shared'
import { buildQrSvg } from '../../../lib/qr'
import { useCampaignDetail } from '../../../hooks/useCampaign'
import { useFormatInstanceCode } from '../../../hooks/useInstanceCode'
import { publicOrigin } from '../../../lib/native/instance'

// [CANV-4] The "show / download / print" hub for the campaign's shared enrollment code. Encodes
// {origin}/join/{code}. Two-tier output (spec D4): (a) a ready-made printable signup sheet with an
// editable headline + subtitle and a fixed, always-accurate scan/typable-code fallback line, printed
// via a hidden iframe so it carries no app chrome; (b) a raw QR image download (PNG primary, SVG
// secondary) as the escape hatch for a custom sign. This modal is also the "pull it up on my phone
// to show a canvasser" view. Self-contained: vendored encoder, no npm dep, no network fetch.

interface SharedCodeQrModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  rawCode: string
}

const DEFAULT_SUBTITLE = 'Canvasser & Volunteer Signup'

export function SharedCodeQrModal({ isOpen, onClose, campaignId, rawCode }: SharedCodeQrModalProps) {
  const { data: campaign } = useCampaignDetail(campaignId)
  // [Native B1] The instance's origin. Null on native with no instance: there is no correct link
  // to encode, so the QR, the fallback line and the download/print actions are withheld.
  const origin = publicOrigin()
  const joinUrl = origin ? `${origin}/join/${rawCode}` : ''
  // [Native B1, NB1-11] The typed fallback carries its server (<orgId>-<CODE>); the QR/link
  // carries it by host, so joinUrl keeps the bare code.
  const typedCode = useFormatInstanceCode()(rawCode)

  const [headline, setHeadline] = useState('')
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE)

  // Seed the headline from the campaign label once it loads, without clobbering an edit in progress.
  useEffect(() => {
    if (campaign?.label) setHeadline((h) => h || campaign.label)
  }, [campaign?.label])

  const effectiveHeadline = headline || campaign?.label || 'Canvass Signup'

  const slug = useMemo(
    () =>
      (campaign?.label ?? 'campaign')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'campaign',
    [campaign?.label],
  )

  const downloadSvg = () => {
    if (!joinUrl) return
    const svg = buildQrSvg(joinUrl, { size: 1024 })
    triggerDownload(new Blob([svg], { type: 'image/svg+xml' }), `join-qr-${slug}.svg`)
  }

  const downloadPng = () => {
    if (!joinUrl) return
    const svg = buildQrSvg(joinUrl, { size: 1024 })
    rasterizeSvgToPng(svg, 1024, (blob) => triggerDownload(blob, `join-qr-${slug}.png`))
  }

  const printSheet = () => {
    if (!origin) return
    const svg = buildQrSvg(joinUrl, { size: 512 })
    printViaIframe(buildPrintSheetHtml(effectiveHeadline, subtitle, svg, origin, typedCode))
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>QR code</Dialog.Header>
          <Dialog.CloseTrigger asChild>
            <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
          </Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch">
              {!!joinUrl && (
                <Box textAlign="center">
                  <QrCode value={joinUrl} size={220} title="Scan to sign up" />
                </Box>
              )}

              <Field.Root>
                <Field.Label>Headline</Field.Label>
                <Input
                  size="sm"
                  value={effectiveHeadline}
                  onChange={(e) => setHeadline(e.target.value)}
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>Subtitle</Field.Label>
                <Input size="sm" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
              </Field.Root>

              {!!origin && (
                <Text fontSize="sm" color="muted">
                  Scan to sign up, or go to {origin}/join and enter code <b>{typedCode}</b>
                </Text>
              )}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack gap={3} flexWrap="wrap">
              <Button variant="outline" size="sm" minH="44px" disabled={!joinUrl} onClick={downloadPng}>
                <FiDownload /> Download PNG
              </Button>
              <Button variant="ghost" size="sm" minH="44px" disabled={!joinUrl} onClick={downloadSvg}>
                Download SVG
              </Button>
              <Button colorPalette="brand" size="sm" minH="44px" disabled={!joinUrl} onClick={printSheet}>
                <FiPrinter /> Print sheet
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}

// ---- download / print helpers (module scope) -------------------------------------------------

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function rasterizeSvgToPng(svg: string, size: number, done: (blob: Blob) => void) {
  const svgUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
  const img = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.fillStyle = 'white' // QR must raster on a solid white bg (fixed contrast, not chrome)
      ctx.fillRect(0, 0, size, size)
      ctx.drawImage(img, 0, 0, size, size)
    }
    URL.revokeObjectURL(svgUrl)
    canvas.toBlob((blob) => {
      if (blob) done(blob)
    }, 'image/png')
  }
  img.src = svgUrl
}

function buildPrintSheetHtml(
  headline: string,
  subtitle: string,
  svg: string,
  origin: string,
  code: string,
): string {
  const esc = (s: string) => s.replace(/[<>&]/g, (c) => (c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;'))
  return `<!doctype html><html><head><meta charset="utf-8"><title>Signup sheet</title>
  <style>
    @page { margin: 0.75in; }
    body { font-family: system-ui, sans-serif; text-align: center; color: #111; }
    h1 { font-size: 40px; margin: 0 0 8px; }
    h2 { font-size: 24px; font-weight: 400; margin: 0 0 32px; color: #333; }
    .qr { width: 60vw; max-width: 480px; margin: 0 auto; }
    .qr svg { width: 100%; height: auto; }
    .fallback { font-size: 18px; margin-top: 28px; }
    .code { font-weight: 700; letter-spacing: 1px; }
  </style></head>
  <body>
    <h1>${esc(headline)}</h1>
    <h2>${esc(subtitle)}</h2>
    <div class="qr">${svg}</div>
    <p class="fallback">Scan to sign up, or go to ${esc(origin)}/join and enter code
      <span class="code">${esc(code)}</span></p>
  </body></html>`
}

function printViaIframe(html: string) {
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)
  const win = iframe.contentWindow
  if (!win) {
    iframe.remove()
    return
  }
  win.document.open()
  win.document.write(html)
  win.document.close()
  win.focus()
  win.print()
  // give the print dialog a tick before tearing down
  setTimeout(() => iframe.remove(), 1000)
}
